import type { CSSProperties } from "react";

export const CIRCLE_ACTION_VARIANTS = [
  "ocean",
  "sky",
  "indigo",
  "violet",
  "fuchsia",
  "rose",
  "sunset",
  "coral",
  "emerald",
  "mint",
  "lime",
  "cyan",
  "gold",
  "slate"
] as const;

export type CircleActionVariant = (typeof CIRCLE_ACTION_VARIANTS)[number];

export interface CircleActionVariantStyle {
  hoverBg: string;
  activeBg: string;
  hoverShadow: string;
  activeShadow: string;
  shadowRgb: string;
  accentColor: string;
  accentColorDark: string;
  glass: {
    tint: string;
    reflect: string;
    moon: string;
    tintDark: string;
    reflectDark: string;
    moonDark: string;
    edge: string;
    edgeDark: string;
    glow: string;
    glowDark: string;
  };
  iconFilterIdle: string;
  iconFilterIdleDark: string;
  fx: {
    blobs: [string, string, string, string];
    particles: string[];
    bloom: string;
  };
}

type VariantSeed = {
  hover: [string, string, string];
  active: [string, string, string];
  shadowRgb: string;
  glass: CircleActionVariantStyle["glass"];
  iconFilterIdle: string;
  iconFilterIdleDark: string;
  fx: CircleActionVariantStyle["fx"];
};

function buildVariant(seed: VariantSeed): CircleActionVariantStyle {
  const [h1, h2, h3] = seed.hover;
  const [a1, a2, a3] = seed.active;
  return {
    hoverBg: `linear-gradient(90deg, ${h1} 0%, ${h2} 50%, ${h3} 100%)`,
    activeBg: `linear-gradient(145deg, ${a1} 0%, ${a2} 52%, ${a3} 100%)`,
    hoverShadow: `0 16px 40px -10px rgba(${seed.shadowRgb}, 0.55)`,
    activeShadow: `0 10px 28px -12px rgba(${seed.shadowRgb}, 0.5)`,
    shadowRgb: seed.shadowRgb,
    accentColor: a1,
    accentColorDark: h1,
    glass: seed.glass,
    iconFilterIdle: seed.iconFilterIdle,
    iconFilterIdleDark: seed.iconFilterIdleDark,
    fx: seed.fx
  };
}

export const CIRCLE_ACTION_VARIANT_STYLES: Record<
  CircleActionVariant,
  CircleActionVariantStyle
> = {
  ocean: buildVariant({
    hover: ["#38bdf8", "#3b82f6", "#4f46e5"],
    active: ["#0ea5e9", "#2563eb", "#4f46e5"],
    shadowRgb: "37, 99, 235",
    glass: {
      tint: "rgba(37, 99, 235, 0.035)",
      reflect: "rgba(37, 99, 235, 0.08)",
      moon: "rgba(37, 99, 235, 0.1)",
      tintDark: "rgba(37, 99, 235, 0.05)",
      reflectDark: "rgba(37, 99, 235, 0.1)",
      moonDark: "rgba(37, 99, 235, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(37, 99, 235, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(42%) sepia(90%) saturate(1200%) hue-rotate(186deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(55%) sepia(60%) saturate(800%) hue-rotate(186deg)",
    fx: {
      blobs: ["34,211,238", "59,130,246", "99,102,241", "14,165,233"],
      particles: ["#67e8f9", "#38bdf8", "#818cf8", "#22d3ee"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.18) 0%, rgba(56,189,248,0.24) 30%, rgba(59,130,246,0.15) 55%, transparent 72%)"
    }
  }),
  sky: buildVariant({
    hover: ["#22d3ee", "#0ea5e9", "#3b82f6"],
    active: ["#06b6d4", "#0ea5e9", "#2563eb"],
    shadowRgb: "14, 165, 233",
    glass: {
      tint: "rgba(14, 165, 233, 0.035)",
      reflect: "rgba(14, 165, 233, 0.08)",
      moon: "rgba(14, 165, 233, 0.1)",
      tintDark: "rgba(14, 165, 233, 0.05)",
      reflectDark: "rgba(14, 165, 233, 0.1)",
      moonDark: "rgba(14, 165, 233, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(14, 165, 233, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(48%) sepia(70%) saturate(900%) hue-rotate(166deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(58%) sepia(50%) saturate(700%) hue-rotate(166deg)",
    fx: {
      blobs: ["34,211,238", "14,165,233", "56,189,248", "6,182,212"],
      particles: ["#67e8f9", "#38bdf8", "#22d3ee", "#7dd3fc"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.17) 0%, rgba(34,211,238,0.23) 30%, rgba(14,165,233,0.14) 55%, transparent 72%)"
    }
  }),
  indigo: buildVariant({
    hover: ["#818cf8", "#4f46e5", "#7c3aed"],
    active: ["#6366f1", "#4f46e5", "#7c3aed"],
    shadowRgb: "79, 70, 229",
    glass: {
      tint: "rgba(79, 70, 229, 0.035)",
      reflect: "rgba(79, 70, 229, 0.08)",
      moon: "rgba(79, 70, 229, 0.1)",
      tintDark: "rgba(79, 70, 229, 0.05)",
      reflectDark: "rgba(79, 70, 229, 0.1)",
      moonDark: "rgba(79, 70, 229, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(79, 70, 229, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(40%) sepia(72%) saturate(1400%) hue-rotate(210deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(52%) sepia(55%) saturate(900%) hue-rotate(210deg)",
    fx: {
      blobs: ["129,140,248", "79,70,229", "99,102,241", "67,56,202"],
      particles: ["#a5b4fc", "#818cf8", "#6366f1", "#c4b5fd"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.17) 0%, rgba(129,140,248,0.23) 30%, rgba(79,70,229,0.15) 55%, transparent 72%)"
    }
  }),
  violet: buildVariant({
    hover: ["#a78bfa", "#9333ea", "#d946ef"],
    active: ["#8b5cf6", "#7e22ce", "#c026d3"],
    shadowRgb: "147, 51, 234",
    glass: {
      tint: "rgba(147, 51, 234, 0.035)",
      reflect: "rgba(147, 51, 234, 0.08)",
      moon: "rgba(147, 51, 234, 0.1)",
      tintDark: "rgba(147, 51, 234, 0.05)",
      reflectDark: "rgba(147, 51, 234, 0.1)",
      moonDark: "rgba(147, 51, 234, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(147, 51, 234, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(38%) sepia(65%) saturate(1800%) hue-rotate(238deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(50%) sepia(50%) saturate(1200%) hue-rotate(238deg)",
    fx: {
      blobs: ["167,139,250", "147,51,234", "236,72,153", "192,132,252"],
      particles: ["#c4b5fd", "#a78bfa", "#e879f9", "#f0abfc"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.16) 0%, rgba(167,139,250,0.23) 30%, rgba(147,51,234,0.15) 55%, transparent 72%)"
    }
  }),
  fuchsia: buildVariant({
    hover: ["#e879f9", "#c026d3", "#ec4899"],
    active: ["#d946ef", "#a21caf", "#db2777"],
    shadowRgb: "192, 38, 211",
    glass: {
      tint: "rgba(192, 38, 211, 0.035)",
      reflect: "rgba(192, 38, 211, 0.08)",
      moon: "rgba(192, 38, 211, 0.1)",
      tintDark: "rgba(192, 38, 211, 0.05)",
      reflectDark: "rgba(192, 38, 211, 0.1)",
      moonDark: "rgba(192, 38, 211, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(192, 38, 211, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(42%) sepia(78%) saturate(1600%) hue-rotate(272deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(54%) sepia(55%) saturate(1100%) hue-rotate(272deg)",
    fx: {
      blobs: ["232,121,249", "192,38,211", "236,72,153", "217,70,239"],
      particles: ["#f0abfc", "#e879f9", "#f472b6", "#d946ef"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.16) 0%, rgba(232,121,249,0.23) 30%, rgba(192,38,211,0.14) 55%, transparent 72%)"
    }
  }),
  rose: buildVariant({
    hover: ["#fb7185", "#e11d48", "#ef4444"],
    active: ["#f43f5e", "#be123c", "#dc2626"],
    shadowRgb: "244, 63, 94",
    glass: {
      tint: "rgba(244, 63, 94, 0.035)",
      reflect: "rgba(244, 63, 94, 0.08)",
      moon: "rgba(244, 63, 94, 0.1)",
      tintDark: "rgba(244, 63, 94, 0.05)",
      reflectDark: "rgba(244, 63, 94, 0.1)",
      moonDark: "rgba(244, 63, 94, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(244, 63, 94, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(48%) sepia(72%) saturate(1100%) hue-rotate(318deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(58%) sepia(55%) saturate(800%) hue-rotate(318deg)",
    fx: {
      blobs: ["251,113,133", "244,63,94", "236,72,153", "225,29,72"],
      particles: ["#fda4af", "#fb7185", "#f472b6", "#fecdd3"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.16) 0%, rgba(251,113,133,0.23) 30%, rgba(244,63,94,0.14) 55%, transparent 72%)"
    }
  }),
  sunset: buildVariant({
    hover: ["#fbbf24", "#f97316", "#fb7185"],
    active: ["#f59e0b", "#ea580c", "#e11d48"],
    shadowRgb: "249, 115, 22",
    glass: {
      tint: "rgba(249, 115, 22, 0.035)",
      reflect: "rgba(249, 115, 22, 0.08)",
      moon: "rgba(249, 115, 22, 0.1)",
      tintDark: "rgba(249, 115, 22, 0.05)",
      reflectDark: "rgba(249, 115, 22, 0.1)",
      moonDark: "rgba(249, 115, 22, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(249, 115, 22, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(52%) sepia(78%) saturate(900%) hue-rotate(346deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(60%) sepia(60%) saturate(700%) hue-rotate(346deg)",
    fx: {
      blobs: ["251,191,36", "249,115,22", "244,63,94", "253,186,116"],
      particles: ["#fcd34d", "#fb923c", "#fb7185", "#fdba74"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.17) 0%, rgba(251,191,36,0.22) 30%, rgba(249,115,22,0.15) 55%, transparent 72%)"
    }
  }),
  coral: buildVariant({
    hover: ["#fb923c", "#ef4444", "#f43f5e"],
    active: ["#f97316", "#dc2626", "#e11d48"],
    shadowRgb: "234, 88, 12",
    glass: {
      tint: "rgba(234, 88, 12, 0.035)",
      reflect: "rgba(234, 88, 12, 0.08)",
      moon: "rgba(234, 88, 12, 0.1)",
      tintDark: "rgba(234, 88, 12, 0.05)",
      reflectDark: "rgba(234, 88, 12, 0.1)",
      moonDark: "rgba(234, 88, 12, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(234, 88, 12, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(50%) sepia(80%) saturate(850%) hue-rotate(352deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(58%) sepia(65%) saturate(650%) hue-rotate(352deg)",
    fx: {
      blobs: ["251,146,60", "234,88,12", "244,63,94", "249,115,22"],
      particles: ["#fdba74", "#fb923c", "#f87171", "#fcd34d"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.16) 0%, rgba(251,146,60,0.22) 30%, rgba(234,88,12,0.14) 55%, transparent 72%)"
    }
  }),
  emerald: buildVariant({
    hover: ["#34d399", "#14b8a6", "#22d3ee"],
    active: ["#10b981", "#0d9488", "#0891b2"],
    shadowRgb: "16, 185, 129",
    glass: {
      tint: "rgba(16, 185, 129, 0.035)",
      reflect: "rgba(16, 185, 129, 0.08)",
      moon: "rgba(16, 185, 129, 0.1)",
      tintDark: "rgba(16, 185, 129, 0.05)",
      reflectDark: "rgba(16, 185, 129, 0.1)",
      moonDark: "rgba(16, 185, 129, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(16, 185, 129, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(48%) sepia(55%) saturate(800%) hue-rotate(128deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(56%) sepia(45%) saturate(650%) hue-rotate(128deg)",
    fx: {
      blobs: ["52,211,153", "16,185,129", "34,211,238", "110,231,183"],
      particles: ["#6ee7b7", "#34d399", "#2dd4bf", "#5eead4"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.17) 0%, rgba(52,211,153,0.23) 30%, rgba(16,185,129,0.15) 55%, transparent 72%)"
    }
  }),
  mint: buildVariant({
    hover: ["#5eead4", "#10b981", "#22c55e"],
    active: ["#14b8a6", "#059669", "#16a34a"],
    shadowRgb: "20, 184, 166",
    glass: {
      tint: "rgba(20, 184, 166, 0.035)",
      reflect: "rgba(20, 184, 166, 0.08)",
      moon: "rgba(20, 184, 166, 0.1)",
      tintDark: "rgba(20, 184, 166, 0.05)",
      reflectDark: "rgba(20, 184, 166, 0.1)",
      moonDark: "rgba(20, 184, 166, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(20, 184, 166, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(52%) sepia(42%) saturate(750%) hue-rotate(142deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(58%) sepia(38%) saturate(600%) hue-rotate(142deg)",
    fx: {
      blobs: ["94,234,212", "20,184,166", "52,211,153", "74,222,128"],
      particles: ["#99f6e4", "#5eead4", "#6ee7b7", "#86efac"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.16) 0%, rgba(94,234,212,0.22) 30%, rgba(20,184,166,0.14) 55%, transparent 72%)"
    }
  }),
  lime: buildVariant({
    hover: ["#bef264", "#84cc16", "#22c55e"],
    active: ["#a3e635", "#65a30d", "#16a34a"],
    shadowRgb: "132, 204, 22",
    glass: {
      tint: "rgba(132, 204, 22, 0.035)",
      reflect: "rgba(132, 204, 22, 0.08)",
      moon: "rgba(132, 204, 22, 0.1)",
      tintDark: "rgba(132, 204, 22, 0.05)",
      reflectDark: "rgba(132, 204, 22, 0.1)",
      moonDark: "rgba(132, 204, 22, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(132, 204, 22, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(58%) sepia(48%) saturate(700%) hue-rotate(68deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(64%) sepia(40%) saturate(550%) hue-rotate(68deg)",
    fx: {
      blobs: ["190,242,100", "132,204,22", "74,222,128", "163,230,53"],
      particles: ["#d9f99d", "#a3e635", "#86efac", "#bef264"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.16) 0%, rgba(190,242,100,0.2) 30%, rgba(132,204,22,0.12) 55%, transparent 72%)"
    }
  }),
  cyan: buildVariant({
    hover: ["#22d3ee", "#14b8a6", "#3b82f6"],
    active: ["#06b6d4", "#0d9488", "#2563eb"],
    shadowRgb: "6, 182, 212",
    glass: {
      tint: "rgba(6, 182, 212, 0.035)",
      reflect: "rgba(6, 182, 212, 0.08)",
      moon: "rgba(6, 182, 212, 0.1)",
      tintDark: "rgba(6, 182, 212, 0.05)",
      reflectDark: "rgba(6, 182, 212, 0.1)",
      moonDark: "rgba(6, 182, 212, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(6, 182, 212, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(50%) sepia(65%) saturate(900%) hue-rotate(158deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(58%) sepia(55%) saturate(700%) hue-rotate(158deg)",
    fx: {
      blobs: ["34,211,238", "6,182,212", "20,184,166", "59,130,246"],
      particles: ["#67e8f9", "#22d3ee", "#5eead4", "#38bdf8"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.16) 0%, rgba(34,211,238,0.22) 30%, rgba(6,182,212,0.14) 55%, transparent 72%)"
    }
  }),
  gold: buildVariant({
    hover: ["#facc15", "#f59e0b", "#f97316"],
    active: ["#eab308", "#d97706", "#ea580c"],
    shadowRgb: "234, 179, 8",
    glass: {
      tint: "rgba(234, 179, 8, 0.035)",
      reflect: "rgba(234, 179, 8, 0.08)",
      moon: "rgba(234, 179, 8, 0.1)",
      tintDark: "rgba(234, 179, 8, 0.05)",
      reflectDark: "rgba(234, 179, 8, 0.1)",
      moonDark: "rgba(234, 179, 8, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(234, 179, 8, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(62%) sepia(68%) saturate(700%) hue-rotate(12deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(68%) sepia(55%) saturate(550%) hue-rotate(12deg)",
    fx: {
      blobs: ["250,204,21", "234,179,8", "251,191,36", "245,158,11"],
      particles: ["#fde047", "#facc15", "#fcd34d", "#fbbf24"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.17) 0%, rgba(250,204,21,0.2) 30%, rgba(234,179,8,0.12) 55%, transparent 72%)"
    }
  }),
  slate: buildVariant({
    hover: ["#94a3b8", "#52525b", "#57534e"],
    active: ["#64748b", "#3f3f46", "#44403c"],
    shadowRgb: "71, 85, 105",
    glass: {
      tint: "rgba(71, 85, 105, 0.035)",
      reflect: "rgba(71, 85, 105, 0.08)",
      moon: "rgba(71, 85, 105, 0.1)",
      tintDark: "rgba(71, 85, 105, 0.05)",
      reflectDark: "rgba(71, 85, 105, 0.1)",
      moonDark: "rgba(71, 85, 105, 0.18)",
      edge: "rgba(255, 255, 255, 0.68)",
      edgeDark: "rgba(255, 255, 255, 0.2)",
      glow: "0 8px 22px -12px rgba(71, 85, 105, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.34)",
      glowDark: "0 10px 28px -14px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.14)"
    },
    iconFilterIdle:
      "brightness(0) saturate(100%) invert(52%) sepia(12%) saturate(500%) hue-rotate(182deg)",
    iconFilterIdleDark:
      "brightness(0) saturate(100%) invert(62%) sepia(8%) saturate(400%) hue-rotate(182deg)",
    fx: {
      blobs: ["148,163,184", "100,116,139", "161,161,170", "120,113,108"],
      particles: ["#cbd5e1", "#94a3b8", "#a1a1aa", "#d6d3d1"],
      bloom:
        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.15) 0%, rgba(148,163,184,0.2) 30%, rgba(100,116,139,0.12) 55%, transparent 72%)"
    }
  })
};

export function isCircleActionVariant(
  value: string | null | undefined
): value is CircleActionVariant {
  return (
    value != null &&
    (CIRCLE_ACTION_VARIANTS as readonly string[]).includes(value)
  );
}

export function resolveCircleActionVariant(
  value: string | null | undefined,
  fallback: CircleActionVariant = "ocean"
): CircleActionVariant {
  return isCircleActionVariant(value) ? value : fallback;
}

export function getCircleActionVariantStyle(
  variant: CircleActionVariant
): CircleActionVariantStyle {
  return CIRCLE_ACTION_VARIANT_STYLES[variant];
}

export function circleActionVariantCssVars(
  variant: CircleActionVariant
): CSSProperties {
  const style = CIRCLE_ACTION_VARIANT_STYLES[variant];
  return {
    "--cab-glass-tint": style.glass.tint,
    "--cab-glass-reflect": style.glass.reflect,
    "--cab-glass-moon": style.glass.moon,
    "--cab-glass-tint-dark": style.glass.tintDark,
    "--cab-glass-reflect-dark": style.glass.reflectDark,
    "--cab-glass-moon-dark": style.glass.moonDark,
    "--cab-glass-edge": style.glass.edge,
    "--cab-glass-edge-dark": style.glass.edgeDark,
    "--cab-glass-glow": style.glass.glow,
    "--cab-glass-glow-dark": style.glass.glowDark,
    "--cab-icon-filter-idle": style.iconFilterIdle,
    "--cab-icon-filter-idle-dark": style.iconFilterIdleDark,
    "--cab-hover-bg": style.hoverBg,
    "--cab-active-bg": style.activeBg,
    "--cab-hover-shadow": style.hoverShadow,
    "--cab-active-shadow": style.activeShadow,
    "--cab-shadow-rgb": style.shadowRgb,
    // GlassBadge active-glass tint variables
    "--gb-tint-bg": `rgba(${style.shadowRgb}, 0.08)`,
    "--gb-tint-bg-hover": `rgba(${style.shadowRgb}, 0.13)`,
    "--gb-tint-border": `rgba(${style.shadowRgb}, 0.24)`,
    "--gb-tint-shadow": "0 1px 4px -2px rgba(15,23,42,0.12)",
    "--gb-accent-color": style.accentColor,
    "--gb-accent-color-dark": style.accentColorDark,
    "--gt-particle-color": style.fx.particles[0] ?? style.accentColor,
    "--gt-particle-color-alt": style.fx.particles[1] ?? style.accentColor,
  } as CSSProperties;
}

/** Variables atenuadas para circle buttons del sidebar (no afecta badges). */
export function sidebarCircleActionVariantCssVars(
  variant: CircleActionVariant
): CSSProperties {
  const style = CIRCLE_ACTION_VARIANT_STYLES[variant];
  const rgb = style.shadowRgb;
  return {
    ...circleActionVariantCssVars(variant),
    "--gb-tint-bg": `rgba(${rgb}, 0.04)`,
    "--gb-tint-bg-hover": `rgba(${rgb}, 0.07)`,
    "--gb-tint-border": `rgba(${rgb}, 0.12)`,
    "--gb-tint-shadow": `0 1px 3px -1px rgba(15,23,42,0.06), 0 2px 6px -3px rgba(${rgb}, 0.10)`,
    "--cab-hover-shadow": `0 14px 36px -12px rgba(${rgb}, 0.36)`,
    "--cab-active-shadow": `0 10px 26px -14px rgba(${rgb}, 0.3)`,
  } as CSSProperties;
}
